import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CompanyContext, UserContext } from 'src/context';

// Whitelist de features bloqueables v?lidas (sincronizar con server_admin / smart_back permission-guard)
const VALID_BLOCKED_FEATURES = [
  'conciliacion',
  'nomina',
  'asiento',
  'f29',
  'pascale_chat',
  'crear_obligacion',
  'abonar',
  'cargar_plan_cuenta',
  'cargar_repositorio',
  'notificar_repositorio',
  'editar_saldos_flujo_caja',
  'export_libro_mayor',
  'export_libro_diario',
  'export_conciliacion',
  'export_eerr',
  'export_flujo_caja',
  'export_reportes_cobranza',
  'export_reportes_proveedores',
  'export_reportes_ventas',
  'export_reportes_compras',
  'export_agenda',
  'export_eoaf',
  'export_documentos',
  'export_api_movimientos',
  'export_api_documentos',
  'export_api_remuneraciones',
  'export_api_libro_mayor',
  'export_api_contactos',
  'export_api_reportes',
  'export_unmasked',
];

const GRANT_IN_BLOCKED_FEATURES = [
  'cargar_plan_cuenta',
  'cargar_repositorio',
  'notificar_repositorio',
  'editar_saldos_flujo_caja',
  'export_unmasked',
];

/**
 * Hook para verificar si una funcionalidad est? bloqueada para la empresa o usuario actual
 * Implementa validaci?n de whitelist para evitar features inv?lidas
 * @returns {Object} - Objeto con funciones para verificar features bloqueadas
 */
export const useBlockedFeatures = () => {
  // Obtener datos de la empresa
  const companyContext = useContext(CompanyContext);
  const reduxCompany = useSelector((state) => state.companyList?.selectedCompany);
  const selectedCompany = companyContext?.selectedCompany || reduxCompany;
  
  // Obtener datos del usuario desde contexto
  const user = useContext(UserContext);
  
  // Obtener blockedFeatures desde Redux auth (actualizado por GetMyPermissions)
  const reduxBlockedFeatures = useSelector((state) => state.auth?.blockedFeatures);
  
  // Combinar features bloqueadas de empresa, usuario y Redux, validando contra whitelist
  const { blockedFeatures, companyBlockedFeatures, userBlockedFeatures } = useMemo(() => {
    // Filtrar solo features v?lidas (defensa en profundidad)
    const validateFeatures = (features) => 
      Array.isArray(features) 
        ? features.filter(f => typeof f === 'string' && VALID_BLOCKED_FEATURES.includes(f))
        : [];
    
    const companyFeatures = validateFeatures(selectedCompany?.blockedFeatures);
    const userFeatures = validateFeatures(user?.blockedFeatures);
    const reduxFeatures = validateFeatures(reduxBlockedFeatures);
    
    // Unir todas las fuentes eliminando duplicados
    const combined = [...new Set([...companyFeatures, ...userFeatures, ...reduxFeatures])];
    
    return {
      blockedFeatures: combined,
      companyBlockedFeatures: companyFeatures,
      userBlockedFeatures: [...new Set([...userFeatures, ...reduxFeatures])],
    };
  }, [selectedCompany?.blockedFeatures, user?.blockedFeatures, reduxBlockedFeatures]);

  /**
   * Verifica si una feature espec?fica est? bloqueada (por empresa o usuario)
   * @param {string} featureId - ID de la feature a verificar (ej: 'conciliacion')
   * @returns {boolean} - true si est? bloqueada, false si no
   */
  const isFeatureBlocked = (featureId) => {
    // Validar que el featureId sea v?lido
    if (typeof featureId !== 'string' || !VALID_BLOCKED_FEATURES.includes(featureId)) {
      console.warn(`useBlockedFeatures: featureId inv?lido: ${featureId}`);
      return false;
    }
    return blockedFeatures.includes(featureId);
  };

  /**
   * Verifica si la conciliaci?n est? bloqueada
   * @returns {boolean}
   */
  const isConciliacionBlocked = () => {
    return isFeatureBlocked('conciliacion');
  };

  /**
   * Verifica si F29 est? bloqueado
   * @returns {boolean}
   */
  const isF29Blocked = () => {
    return isFeatureBlocked('f29');
  };

  const crearObligacionBlocked = useMemo(
    () => blockedFeatures.includes('crear_obligacion'),
    [blockedFeatures],
  );

  const abonarBlocked = useMemo(
    () => blockedFeatures.includes('abonar'),
    [blockedFeatures],
  );

  const isCrearObligacionBlocked = () => crearObligacionBlocked;

  const isAbonarBlocked = () => abonarBlocked;

  const canCargarPlanCuenta = () => {
    const userBf = user?.blockedFeatures ?? reduxBlockedFeatures;
    return Array.isArray(userBf) && userBf.includes('cargar_plan_cuenta');
  };

  const hasAdminOrSuperColaboradorRole = () => {
    const roles = Array.isArray(user?.roles) ? user.roles : user?.roles ? [user.roles] : [];
    return roles.includes('ADMIN') || roles.includes('SUPER_COLABORADOR');
  };

  const canCargarRepositorio = () => {
    if (hasAdminOrSuperColaboradorRole()) return true;
    const userBf = user?.blockedFeatures ?? reduxBlockedFeatures;
    return Array.isArray(userBf) && userBf.includes('cargar_repositorio');
  };

  const canNotificarRepositorio = () => {
    if (hasAdminOrSuperColaboradorRole()) return true;
    const userBf = user?.blockedFeatures ?? reduxBlockedFeatures;
    return Array.isArray(userBf) && userBf.includes('notificar_repositorio');
  };

  const canEditarSaldosFlujoCaja = () => {
    const roles = Array.isArray(user?.roles) ? user.roles : user?.roles ? [user.roles] : [];
    if (
      roles.includes('ADMIN') ||
      roles.includes('SUPER_COLABORADOR') ||
      roles.includes('COLABORADOR')
    ) {
      return true;
    }
    const userBf = user?.blockedFeatures ?? reduxBlockedFeatures;
    return Array.isArray(userBf) && userBf.includes('editar_saldos_flujo_caja');
  };

  return {
    blockedFeatures,
    companyBlockedFeatures,
    userBlockedFeatures,
    crearObligacionBlocked,
    abonarBlocked,
    isFeatureBlocked,
    isConciliacionBlocked,
    isF29Blocked,
    isCrearObligacionBlocked,
    isAbonarBlocked,
    canCargarPlanCuenta,
    canCargarRepositorio,
    canNotificarRepositorio,
    canEditarSaldosFlujoCaja,
    VALID_BLOCKED_FEATURES,
    GRANT_IN_BLOCKED_FEATURES,
  };
};

export default useBlockedFeatures;
